import React, {createContext, useContext} from 'react';
import {useDispatch} from 'react-redux';
import {Form, FormikProvider, useFormik} from 'formik';
import {useNavigate} from 'react-router-dom';
import {SHA256} from 'crypto-js';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useAuthContext} from '../../../../common/components/core/AuthContext';
import {setToken, setUser} from '../../redux/actions/authActions';
import {AppDispatch} from '../../../../store/configureStore';
import {
  actionLogMessages as M,
  actionPages as AP, actionResults as R,
  actionSubjects as S,
  actionTypes as T,
  pathnames as P
} from '../../../../common/enums';
import {getErrorMessage} from '../../../../common/utils';

export type LoginAsAdminContext = {[key: string]: never};

export type LoginAsAdminContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: LoginAsAdminContext = {};

export type LoginAsAdminFormValues = {
  admin: string
  password: string
  targetUser: string
}

export const defaultFormValues: LoginAsAdminFormValues = {
  admin: '',
  password: '',
  targetUser: '',
};

export const LoginAsAdminContext = createContext<LoginAsAdminContext>(defaultContext);
LoginAsAdminContext.displayName = 'LoginAsAdminContext';

export const useLoginAsAdminContext = () => useContext(LoginAsAdminContext);

export const LoginAsAdminContextProvider = ({children}: LoginAsAdminContextProviderProps) => {
  const {apiService} = useGlobalContext();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const {handleLogin} = useAuthContext();
  const searchParams = new URLSearchParams(window.location.search);

  const formik = useFormik<LoginAsAdminFormValues>({
    initialValues: {...defaultFormValues, targetUser: searchParams.get('username') || ''},
    validate: (values) => {
      const errors: Partial<{ [key in keyof LoginAsAdminFormValues]: string }> = {};
      if (!values.admin) errors.admin = E.requiredField;
      if (!values.password) errors.password = E.requiredField;
      if (!values.targetUser) errors.targetUser = E.requiredField;

      return errors;
    },
    onSubmit: async (values) => {
      try {
        const encryptedPassword = SHA256(values.password);
        const result = await apiService.loginAsAdmin(values.admin, encryptedPassword.toString(), values.targetUser);
        const token = result.body.token;
        const user = result.body.user;
        if (result.errorCode < 0) {
          globalMessage.open(getErrorMessage(result.errorCode, E));
        } else {
          if (token) {
            dispatch(setToken(token));
            apiService.setToken(token);
          }
          if (user) {
            dispatch(setUser(user));
          }
          await handleLogin();
          navigate(P.HOME);

          // if (token && user) {
          //   await apiService.logUserAction(
          //     AP.AUTH,
          //     S.LOGIN_AS_ADMIN,
          //     T.GENERAL,
          //     M.LOGIN_AS_ADMIN(values.admin),
          //     result.errorCode === 0 ? R.SUCCESS : R.FAIL
          //   )
          // }
        }
      } catch (e) {
        globalMessage.open(E.serverError);
        dispatch(setToken(''));
        dispatch(setUser(null));
      }
    },
    enableReinitialize: true,
  });

  const context: LoginAsAdminContext = {
    ...defaultContext,
  };

  return (
    <LoginAsAdminContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </LoginAsAdminContext.Provider>
  );
};
